import React from 'react';
import { ChevronDown, CloudSnow, Wind } from 'lucide-react';
import { DateTime } from 'luxon';
import { IWeatherItem } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useDistanceFormatter, useSnowFormat, useTemperatureFormatter } from '@/i18n/unitFormat';
import { mpsToKmh } from '@/i18n/units';
import { SHORT_DATE_FORMAT } from '@/util/formatDate';
import { TIME_FORMAT } from '@/util/formatAgo';
import { weatherTypeMap } from '@/util/weatherTypeMap';
import { cn } from '@/util/cn';
import { Text } from '@/components/ui/text';
import { ConditionIcon } from './ConditionIcon';
import { ElevationKey, pickLevel } from './ElevationToggle';

export const WeatherDayRow = ({
  day, hourly, elevation, expanded, onToggle,
}: {
  day: IWeatherItem;
  hourly: IWeatherItem[];
  elevation: ElevationKey;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const { t } = useOTSTranslation('common');
  const formatTemp = useTemperatureFormatter('temperature');
  const formatDistance = useDistanceFormatter('kmhOrMileh');
  const formatSnow = useSnowFormat();
  const lvl = pickLevel(day, elevation);
  const hasHourly = hourly.length > 0;
  const dt = DateTime.fromISO(day.datetime);
  const wind = (s?: number) => (s !== undefined
    ? formatDistance(mpsToKmh(s), undefined, undefined, true, true)
    : '—');

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasHourly}
        aria-expanded={hasHourly ? expanded : undefined}
        className={cn(
          'w-full px-4 py-4 text-left transition',
          hasHourly ? 'hover:bg-muted/20' : 'cursor-default',
        )}
      >
        {/* Mobile (<sm): stacked card. */}
        <div className="sm:hidden">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-heading text-lg font-bold leading-none">{dt.toFormat('EEE')}</div>
              <Text variant="subtle" className="mt-1 text-xs text-muted-foreground">
                {dt.toFormat(SHORT_DATE_FORMAT)}
              </Text>
            </div>
            {hasHourly && (
              <ChevronDown
                size={18}
                strokeWidth={2.5}
                className={cn('mt-1 transition', expanded ? 'text-foreground rotate-180' : 'text-muted-foreground')}
              />
            )}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <ConditionIcon type={lvl?.type} />
            <span className="text-sm text-foreground/90">
              {lvl?.type ? t(weatherTypeMap[lvl.type]) : '—'}
            </span>
          </div>
          {/* Labels carry min-w-0 + break-words for the same reason as the sm+
              grid below: a long translated label must wrap inside its own
              half-width cell instead of pushing the value out of it. */}
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            <div className="flex items-baseline justify-between gap-2">
              <dt className="min-w-0 break-words text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('resort.weather.high')}</dt>
              <dd className="font-semibold tabular-nums">
                {lvl?.temp?.max !== undefined ? formatTemp(lvl.temp.max) : '—'}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="min-w-0 break-words text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('resort.weather.wind')}</dt>
              <dd className="font-semibold tabular-nums">{wind(lvl?.wind?.speed)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="min-w-0 break-words text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('resort.weather.low')}</dt>
              <dd className="font-semibold tabular-nums text-muted-foreground">
                {lvl?.temp?.min !== undefined ? formatTemp(lvl.temp.min) : '—'}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="min-w-0 break-words text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('resort.weather.snow')}</dt>
              <dd className="font-semibold tabular-nums">
                {formatSnow(lvl?.snow?.snowfall ?? 0)}
              </dd>
            </div>
            {lvl?.wind?.gusts !== undefined && (
              <div className="col-span-2 flex items-baseline justify-end gap-2">
                <dt className="text-xs text-muted-foreground">
                  {t('resort.weather.gusts', { speed: wind(lvl.wind.gusts) })}
                </dt>
              </div>
            )}
          </dl>
        </div>

        {/* sm+: 12-col grid. Col-spans mirror the header row in weather.tsx —
            change both together. Every cell holding translated text carries
            `min-w-0` so it wraps inside its 1/12 track rather than overflowing
            into the next column (a grid item's default min-width:auto lets
            long words such as de "Windgeschwindigkeit" spill over). */}
        <div className="hidden sm:grid sm:grid-cols-12 sm:items-center sm:gap-3">
          <div className="col-span-2 min-w-0">
            <div className="font-heading text-lg font-bold leading-none">{dt.toFormat('EEE')}</div>
            <Text variant="subtle" className="mt-1 text-xs text-muted-foreground">
              {dt.toFormat(SHORT_DATE_FORMAT)}
            </Text>
          </div>
          <div className="col-span-3 flex min-w-0 items-center gap-2">
            <ConditionIcon type={lvl?.type} />
            <span className="min-w-0 break-words text-sm">
              {lvl?.type ? t(weatherTypeMap[lvl.type]) : '—'}
            </span>
          </div>
          <div className="col-span-1 min-w-0 text-sm font-semibold tabular-nums">
            {lvl?.temp?.max !== undefined ? formatTemp(lvl.temp.max) : '—'}
          </div>
          <div className="col-span-1 min-w-0 text-sm font-semibold tabular-nums text-muted-foreground">
            {lvl?.temp?.min !== undefined ? formatTemp(lvl.temp.min) : '—'}
          </div>
          <div className="col-span-2 min-w-0 text-sm">
            <div className="font-semibold tabular-nums">{wind(lvl?.wind?.speed)}</div>
            {lvl?.wind?.gusts !== undefined && (
              <Text variant="subtle" className="break-words text-xs text-muted-foreground">
                {t('resort.weather.gusts', { speed: wind(lvl.wind.gusts) })}
              </Text>
            )}
          </div>
          <div className="col-span-2 min-w-0 text-sm">
            <div className="font-semibold tabular-nums">
              {formatSnow(lvl?.snow?.snowfall ?? 0)}
            </div>
          </div>
          <div className="col-span-1 flex justify-end">
            {hasHourly && (
              <ChevronDown
                size={18}
                strokeWidth={2.5}
                className={cn('transition', expanded ? 'text-foreground rotate-180' : 'text-muted-foreground')}
              />
            )}
          </div>
        </div>
      </button>

      {/* Expanded — hourly rows for this day */}
      {expanded && (
        <div className="border-t border-border bg-muted/30 px-4 py-3">
          {hourly.length === 0 ? (
            <Text variant="small" className="py-2 text-center text-muted-foreground">
              {t('common.na')}
            </Text>
          ) : (
            <>
              {/* Mobile-only sticky date. One day's hourly list runs well past a
                  phone screen, so by the time you reach the afternoon rows the
                  day they belong to has scrolled away. This strip repeats it and
                  pins to the top of the viewport. `sticky` is bounded by its
                  containing block — this panel — so it rides in with the day and
                  releases as the day scrolls out instead of hanging over the
                  next one; only one is ever mounted, since weather.tsx tracks a
                  single `expandedKey`. It also needs no ancestor to be a scroll
                  container: see the `overflow-clip` note on the SectionCard in
                  weather.tsx before changing overflow anywhere above this.
                  `aria-hidden` because the toggle button above already carries
                  the same date in its accessible name. */}
              <div
                aria-hidden
                className={cn(
                  'sticky top-0 z-10 -mx-4 -mt-3 mb-3 flex items-baseline gap-2',
                  'border-b border-border bg-card px-4 py-2 sm:hidden',
                )}
              >
                <span className="font-heading text-sm font-bold leading-none">
                  {dt.toFormat('EEE')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {dt.toFormat(SHORT_DATE_FORMAT)}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      <th className="py-2 pr-4">{t('resort.weather.time')}</th>
                      <th className="py-2 pr-4" aria-label={t('nav.tab.weather')} />
                      <th className="py-2 pr-4">{t('resort.weather.temp')}</th>
                      <th className="py-2 pr-4">
                        <span className="inline-flex items-center gap-1">
                          <Wind size={12} aria-hidden />
                          {t('common.windSpeed')}
                        </span>
                      </th>
                      <th className="py-2">
                        <span className="inline-flex items-center gap-1">
                          <CloudSnow size={12} aria-hidden />
                          {t('region.skiReport.snowfall')}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {hourly.map((hour) => {
                      const h = pickLevel(hour, elevation);
                      return (
                        <tr key={hour.datetime} className="border-t border-border/60">
                          <td className="whitespace-nowrap py-2 pr-4 text-sm font-semibold tabular-nums">
                            {DateTime.fromISO(hour.datetime).toFormat(TIME_FORMAT)}
                          </td>
                          <td className="py-2 pr-4">
                            <div className="flex items-center gap-2">
                              <ConditionIcon type={h?.type} />
                              <span className="text-sm text-foreground/85">
                                {h?.type ? t(weatherTypeMap[h.type]) : '—'}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 pr-4 text-sm tabular-nums">
                            {h?.temp?.max !== undefined ? formatTemp(h.temp.max) : '—'}
                          </td>
                          <td className="py-2 pr-4 text-sm tabular-nums">{wind(h?.wind?.speed)}</td>
                          <td className="py-2 text-sm tabular-nums">
                            {formatSnow(h?.snow?.snowfall ?? 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
